
import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

interface ErrorContext {
  action?: string;
  component?: string;
  userId?: string;
  operation?: string;
  tutorial?: string;
  currentView?: string;
  attempts?: number;
  metadata?: Record<string, any>;
}

interface ErrorHandlerOptions {
  showToast?: boolean;
  logToConsole?: boolean;
  context?: ErrorContext;
}

export const useErrorHandler = () => {
  const { toast } = useToast();

  const handleError = useCallback((
    error: Error | unknown,
    options: ErrorHandlerOptions = {}
  ) => {
    const {
      showToast = true,
      logToConsole = true,
      context = {}
    } = options;

    // Normalize error
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    
    // Create error object with context
    const errorWithContext = {
      message: normalizedError.message,
      stack: normalizedError.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...context
    };

    // Log to console in development
    if (logToConsole && process.env.NODE_ENV === 'development') {
      console.group('🚨 Error Handler');
      console.error('Error:', normalizedError);
      console.error('Context:', errorWithContext);
      console.groupEnd();
    }

    // Show user-friendly toast notification
    if (showToast) {
      let title = 'Something went wrong';
      let description = 'Please try again or contact support if the problem persists.';

      // Customize messages for common errors
      if (normalizedError.message.includes('network') || normalizedError.message.includes('fetch')) {
        title = 'Connection Error';
        description = 'Please check your internet connection and try again.';
      } else if (normalizedError.message.includes('permission') || normalizedError.message.includes('unauthorized')) {
        title = 'Permission Denied';
        description = 'You may need to log in or contact support for access.';
      } else if (normalizedError.message.includes('validation') || normalizedError.message.includes('invalid')) {
        title = 'Invalid Data';
        description = 'Please check your input and try again.';
      }

      toast({
        title,
        description,
        variant: 'destructive',
      });
    }

    return errorWithContext;
  }, [toast]);

  const handleAsyncError = useCallback(async (
    asyncFn: () => Promise<any>,
    options: ErrorHandlerOptions = {}
  ) => {
    try {
      return await asyncFn();
    } catch (error) {
      handleError(error, options);
      throw error; // Re-throw so calling code can handle if needed
    }
  }, [handleError]);

  return {
    handleError,
    handleAsyncError
  };
};
